(() => {
  if (customElements.get('ownart-product-compare')) return;

  class OwnArtProductCompare extends HTMLElement {
    constructor() {
      super();
      this.handleScopeClick = this.handleScopeClick.bind(this);
      this.handleComponentClick = this.handleComponentClick.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleExternalUpdate = this.handleExternalUpdate.bind(this);
      this.handleMutations = this.handleMutations.bind(this);
      this.handleCatalogFilterUpdate = this.handleCatalogFilterUpdate.bind(this);
      this.memorySelection = [];
    }

    connectedCallback() {
      this.scope = document.getElementById(this.dataset.compareScope);
      if (!this.scope) return;

      this.storageKey = this.dataset.storageKey || 'ownart-product-compare';
      this.limit = Math.max(2, Number.parseInt(this.dataset.compareLimit || '3', 10) || 3);
      this.bar = this.querySelector('[data-compare-bar]');
      this.count = this.querySelector('[data-compare-count]');
      this.selectionList = this.querySelector('[data-compare-selection]');
      this.openButton = this.querySelector('[data-compare-open]');
      this.pageLink = this.querySelector('[data-compare-page-link]');
      this.status = this.querySelector('[data-compare-status]');
      this.dialog = this.querySelector('[data-compare-dialog]');
      this.dialogContent = this.querySelector('[data-compare-dialog-content]');

      this.scope.addEventListener('click', this.handleScopeClick);
      this.addEventListener('click', this.handleComponentClick);
      this.dialog?.addEventListener('click', this.handleDialogClick);
      window.addEventListener('ownart:product-compare-updated', this.handleExternalUpdate);
      window.addEventListener('ownart:catalog-filter-updated', this.handleCatalogFilterUpdate);
      this.observer = new MutationObserver(this.handleMutations);
      this.observer.observe(this.scope, { childList: true, subtree: true });
      this.render();
    }

    disconnectedCallback() {
      this.scope?.removeEventListener('click', this.handleScopeClick);
      this.removeEventListener('click', this.handleComponentClick);
      this.dialog?.removeEventListener('click', this.handleDialogClick);
      window.removeEventListener('ownart:product-compare-updated', this.handleExternalUpdate);
      window.removeEventListener('ownart:catalog-filter-updated', this.handleCatalogFilterUpdate);
      this.observer?.disconnect();
    }

    get labels() {
      return {
        add: this.dataset.labelAdd || 'Compare',
        selected: this.dataset.labelSelected || 'Selected',
        remove: this.dataset.labelRemove || 'Remove [product]',
        count: this.dataset.labelCount || '[count] / [limit] selected',
        open: this.dataset.labelOpen || 'Compare products',
        chooseTwo: this.dataset.labelChooseTwo || 'Select at least two products to compare.',
        limit: this.dataset.labelLimit || 'You can compare up to [limit] products at a time.',
        price: this.dataset.labelPrice || 'Price',
        availability: this.dataset.labelAvailability || 'Availability',
        available: this.dataset.labelAvailable || 'Available',
        soldOut: this.dataset.labelSoldOut || 'Sold out',
        category: this.dataset.labelCategory || 'Category',
        product: this.dataset.labelProduct || 'Product',
        style: this.dataset.labelStyle || 'Style / category',
        material: this.dataset.labelMaterial || 'Material',
        stone: this.dataset.labelStone || 'Stone',
        setting: this.dataset.labelSetting || 'Setting',
        sizeLength: this.dataset.labelSizeLength || 'Size / length',
        weight: this.dataset.labelWeight || 'Weight',
        dimensions: this.dataset.labelDimensions || 'Dimensions',
        brand: this.dataset.labelBrand || 'Brand',
        details: this.dataset.labelDetails || 'Details',
        viewProduct: this.dataset.labelViewProduct || 'View product',
        notAvailable: this.dataset.labelNotAvailable || 'Not available',
        close: this.dataset.labelClose || 'Close'
      };
    }

    normalizeProduct(value) {
      if (!value || value.id === undefined || value.id === null) return null;

      const options = Array.isArray(value.options)
        ? value.options
            .map((option) => ({
              name: String(option?.name || '').trim(),
              values: Array.isArray(option?.values)
                ? option.values.map((item) => String(item || '').trim()).filter(Boolean)
                : String(option?.value || '').split(',').map((item) => item.trim()).filter(Boolean)
            }))
            .filter((option) => option.name)
        : [];
      const details = Array.isArray(value.details) ? value.details.map((item) => String(item || '').trim()).filter(Boolean) : [];
      const firstDetail = (...needles) => details.find((detail) => {
        const normalized = detail.toLowerCase();
        return needles.some((needle) => normalized.includes(needle));
      }) || '';
      const readString = (...values) => {
        const valueToUse = values.find((item) => item !== undefined && item !== null && String(item).trim() && String(item).trim() !== '0');
        return valueToUse === undefined ? '' : String(valueToUse).trim();
      };

      return {
        id: String(value.id),
        title: String(value.title || '').trim(),
        url: String(value.url || '').trim(),
        image: String(value.image || '').trim(),
        imageAlt: String(value.imageAlt || value.image_alt || value.title || '').trim(),
        priceFormatted: String(value.priceFormatted || value.price_formatted || value.price || '').trim(),
        compareAtPriceFormatted: String(value.compareAtPriceFormatted || value.compare_at_price_formatted || '').trim(),
        available: value.available === true || value.available === 'true',
        type: String(value.type || '').trim(),
        style: readString(value.style, value.type),
        vendor: String(value.vendor || '').trim(),
        weight: readString(value.weight, value.weight_grams),
        material: readString(value.material, firstDetail('gold', 'silver', 'plated', 'vermeil', 'steel')),
        stone: readString(value.stone, firstDetail('synthetic', 'cz', 'zirconia', 'pearl', 'diamond', 'gemstone')),
        setting: readString(value.setting, firstDetail('setting')),
        dimensions: readString(value.dimensions),
        options,
        details
      };
    }

    getSelection() {
      try {
        const stored = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
        if (!Array.isArray(stored)) return [];
        return stored.map((item) => this.normalizeProduct(item)).filter(Boolean).slice(0, this.limit);
      } catch (error) {
        return this.memorySelection;
      }
    }

    saveSelection(selection) {
      const normalized = selection.map((item) => this.normalizeProduct(item)).filter(Boolean).slice(0, this.limit);
      this.memorySelection = normalized;
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(normalized));
      } catch (error) {}
      this.render();
      window.dispatchEvent(new CustomEvent('ownart:product-compare-updated'));
    }

    readProduct(button) {
      const card = button.closest('[data-product-compare-card]');
      const source = card?.querySelector('script[data-product-compare-data]');
      if (!source?.textContent) return null;

      try {
        return this.normalizeProduct(JSON.parse(source.textContent));
      } catch (error) {
        return null;
      }
    }

    handleScopeClick(event) {
      const button = event.target.closest('[data-product-compare-toggle]');
      if (!button || !this.scope.contains(button)) return;
      event.preventDefault();

      const product = this.readProduct(button);
      if (!product) return;

      const selection = this.getSelection();
      const selectedIndex = selection.findIndex((item) => item.id === product.id);
      if (selectedIndex >= 0) {
        selection.splice(selectedIndex, 1);
        this.setStatus('');
        this.saveSelection(selection);
        return;
      }

      if (selection.length >= this.limit) {
        this.setStatus(this.labels.limit.replace('[limit]', String(this.limit)));
        return;
      }

      selection.push(product);
      this.setStatus('');
      this.saveSelection(selection);
    }

    handleComponentClick(event) {
      const removeButton = event.target.closest('[data-compare-remove]');
      if (removeButton && this.contains(removeButton)) {
        const selection = this.getSelection().filter((item) => item.id !== removeButton.dataset.compareRemove);
        this.setStatus('');
        this.saveSelection(selection);
        return;
      }

      const openButton = event.target.closest('[data-compare-open]');
      if (openButton && this.contains(openButton)) {
        const selection = this.getSelection();
        if (selection.length < 2) {
          this.setStatus(this.labels.chooseTwo);
          return;
        }
        this.openDialog();
        return;
      }

      const pageLink = event.target.closest('[data-compare-page-link]');
      if (pageLink && this.contains(pageLink)) {
        this.syncComparePageLink();
        return;
      }

      const closeButton = event.target.closest('[data-compare-close]');
      if (closeButton && this.contains(closeButton)) this.closeDialog();
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) this.closeDialog();
    }

    handleExternalUpdate() {
      this.render();
    }

    handleCatalogFilterUpdate() {
      this.syncComparePageLink();
    }

    handleMutations() {
      this.syncControls();
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    openDialog() {
      if (!this.dialog) return;
      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
    }

    closeDialog() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
    }

    render() {
      const selection = this.getSelection();
      this.syncComparePageLink();
      if (this.bar) this.bar.hidden = selection.length === 0;
      if (this.count) this.count.textContent = this.labels.count.replace('[count]', String(selection.length)).replace('[limit]', String(this.limit));
      if (this.openButton) this.openButton.disabled = selection.length < 2;
      this.renderSelection(selection);
      this.renderDialog(selection);
      this.syncControls();
    }

    syncComparePageLink() {
      const pageLinks = Array.from(document.querySelectorAll('[data-compare-page-link]'));
      if (!pageLinks.length || !this.dataset.comparePageUrl) return;

      const currentUrl = new URL(window.location.href);
      const compareUrl = new URL(this.dataset.comparePageUrl, currentUrl.origin);
      if (compareUrl.origin !== currentUrl.origin) return;

      const returnTo = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
      compareUrl.searchParams.set('return_to', returnTo);
      pageLinks.forEach((pageLink) => {
        pageLink.href = `${compareUrl.pathname}${compareUrl.search}${compareUrl.hash}`;
      });
    }

    renderSelection(selection) {
      if (!this.selectionList) return;
      const fragment = document.createDocumentFragment();

      selection.forEach((product) => {
        const item = document.createElement('div');
        item.className = 'ownart-product-compare__item';

        if (product.image) {
          const image = document.createElement('img');
          image.className = 'ownart-product-compare__item-image';
          image.src = product.image;
          image.alt = product.imageAlt;
          item.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'ownart-product-compare__item-image';
          item.append(placeholder);
        }

        const title = document.createElement('span');
        title.className = 'ownart-product-compare__item-title';
        title.textContent = product.title;
        item.append(title);

        const remove = document.createElement('button');
        remove.className = 'ownart-product-compare__item-remove';
        remove.type = 'button';
        remove.dataset.compareRemove = product.id;
        remove.setAttribute('aria-label', this.labels.remove.replace('[product]', product.title));
        remove.textContent = 'x';
        item.append(remove);
        fragment.append(item);
      });

      this.selectionList.replaceChildren(fragment);
    }

    renderDialog(selection) {
      if (!this.dialogContent) return;
      const matrix = document.createElement('div');
      matrix.className = 'ownart-product-compare__matrix';
      matrix.style.setProperty('--ownart-compare-columns', String(selection.length));

      const headerLabel = document.createElement('div');
      headerLabel.className = 'ownart-product-compare__cell ownart-product-compare__label';
      headerLabel.textContent = this.labels.product;
      matrix.append(headerLabel);

      selection.forEach((product) => {
        const cell = document.createElement('div');
        cell.className = 'ownart-product-compare__cell ownart-product-compare__product';

        if (product.image) {
          const image = document.createElement('img');
          image.className = 'ownart-product-compare__product-image';
          image.src = product.image;
          image.alt = product.imageAlt;
          cell.append(image);
        }

        const title = document.createElement('h3');
        title.className = 'ownart-product-compare__product-title';
        title.textContent = product.title;
        cell.append(title);

        if (product.url) {
          const link = document.createElement('a');
          link.className = 'ownart-product-compare__product-link';
          link.href = product.url;
          link.textContent = this.labels.viewProduct;
          cell.append(link);
        }
        matrix.append(cell);
      });

      this.appendRow(matrix, this.labels.price, selection.map((product) => {
        if (!product.compareAtPriceFormatted) return product.priceFormatted || this.labels.notAvailable;
        return `${product.priceFormatted} (${product.compareAtPriceFormatted})`;
      }));
      this.appendRow(matrix, this.labels.availability, selection.map((product) => product.available ? this.labels.available : this.labels.soldOut));
      this.appendRow(matrix, this.labels.style, selection.map((product) => product.style || product.type || this.labels.notAvailable));
      this.appendRow(matrix, this.labels.material, selection.map((product) => product.material || this.labels.notAvailable));
      this.appendRow(matrix, this.labels.stone, selection.map((product) => product.stone || this.labels.notAvailable));
      this.appendRow(matrix, this.labels.setting, selection.map((product) => product.setting || this.labels.notAvailable));
      this.appendRow(matrix, this.labels.sizeLength, selection.map((product) => this.formatSizingOptions(product) || this.labels.notAvailable));
      this.appendRow(matrix, this.labels.weight, selection.map((product) => product.weight || this.labels.notAvailable));
      this.appendRow(matrix, this.labels.dimensions, selection.map((product) => product.dimensions || this.labels.notAvailable));

      const otherOptionNames = [];
      selection.forEach((product) => product.options.forEach((option) => {
        if (!this.isSizingOption(option.name) && !otherOptionNames.includes(option.name)) otherOptionNames.push(option.name);
      }));
      otherOptionNames.forEach((name) => {
        this.appendRow(matrix, name, selection.map((product) => product.options.find((option) => option.name === name)?.values.join(', ') || this.labels.notAvailable));
      });

      this.dialogContent.replaceChildren(matrix);
    }

    isSizingOption(name) {
      const normalized = String(name || '').toLowerCase();
      return normalized.includes('size') || normalized.includes('length') || normalized.includes('dimension') || normalized.includes('尺寸') || normalized.includes('长度');
    }

    formatSizingOptions(product) {
      return product.options
        .filter((option) => this.isSizingOption(option.name))
        .map((option) => `${option.name}: ${option.values.join(', ')}`)
        .join('; ');
    }

    appendRow(matrix, label, values) {
      const labelCell = document.createElement('div');
      labelCell.className = 'ownart-product-compare__cell ownart-product-compare__label';
      labelCell.textContent = label;
      matrix.append(labelCell);

      values.forEach((value) => {
        const valueCell = document.createElement('div');
        valueCell.className = 'ownart-product-compare__cell';
        valueCell.textContent = value;
        matrix.append(valueCell);
      });
    }

    syncControls() {
      if (!this.scope) return;
      const selectedIds = new Set(this.getSelection().map((product) => product.id));
      this.scope.querySelectorAll('[data-product-compare-toggle]').forEach((button) => {
        const isSelected = selectedIds.has(String(button.dataset.compareProductId || ''));
        const nextPressed = isSelected ? 'true' : 'false';
        if (button.getAttribute('aria-pressed') !== nextPressed) {
          button.setAttribute('aria-pressed', nextPressed);
        }
        const label = button.querySelector('[data-compare-toggle-label]');
        const nextLabel = isSelected ? this.labels.selected : this.labels.add;
        if (label && label.textContent !== nextLabel) {
          label.textContent = nextLabel;
        }
      });
    }
  }

  customElements.define('ownart-product-compare', OwnArtProductCompare);
})();
